import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import DateInput from "../../components/ui/datePicker";
import Loader from "../../components/ui/Loader";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import {
  DriverProfileStatus,
  DriverStatusLabels,
  RangeEnum,
} from "../../helpers/enums";
import { Chip, pluralize } from "../../helpers/general";
import { useGetDriverSessions } from "../../hooks/drivers/useDriverSessions";
import { useDebounce } from "../../hooks/useDebounce";
import { general } from "../../locales/general";
import SimpleDriversTable from "./Activitiestable";
import qs from "qs";
import { useLocation, useNavigate } from "react-router-dom";
import { cardStyle } from "../Places/PlacesTable";
import { FilterField, PillSelect } from "../Bookings/BookingFilters";
import { controlStyle } from "../../helpers/constants";

export function DriverSessionsList() {
  const { search } = useLocation();
  const queryParams = qs.parse(search.replace(/^\?/, ""));
  const initialPage = parseInt(queryParams.page as string) || 1;
  const initialDate = queryParams.date as string;
  const initialRange = queryParams.range as string;
  const initialStatus = queryParams.status as string;
  const initialNotLoggedIn = queryParams.notLoggedIn as string;
  const navigate = useNavigate();

  const [page, setPage] = useState(initialPage);
  const [date, setDate] = useState<string>(initialDate);
  const [notLogedIn, setNotLogedIn] = useState<number | undefined>(
    initialNotLoggedIn ? parseInt(initialNotLoggedIn, 10) : undefined,
  );
  const [searchQuery, setSearchQuery] = useState(queryParams.query as string);

  // const [notLogedIn, setNotLogedIn] = useState<number | undefined>(undefined);
  // const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"default" | "sessionsCount">("default");
  const [range, setRange] = useState<RangeEnum>(initialRange as RangeEnum);
  const [status, setStatus] = useState<DriverProfileStatus>(
    (initialStatus as DriverProfileStatus) ?? DriverProfileStatus.APPROVED,
  );

  const addQuery = (key: string, value: any) => {
    const newQuery = {
      ...queryParams,
      [key]: value === "" ? undefined : value,
      page: key === "page" ? value : 1,
    };
    const newSearch = qs.stringify(newQuery, { skipNulls: true });
    navigate(`?${newSearch}`, { replace: true });
  };

  useEffect(() => {
    const newQuery = {
      ...queryParams,
      page: page.toString(),
    };
    const newSearch = qs.stringify(newQuery, { skipNulls: true });
    navigate(`?${newSearch}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSortChange = useCallback(
    (newSort: "default" | "sessionsCount") => {
      setSortBy(newSort);
      setPage(1);
      // addQuery("sortBy", newSort);
    },
    [],
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const filter = useMemo(
    () => ({
      date,
      notLogedIn,
      page,
      limit: 10,
      query: debouncedQuery || undefined,
      sortBy: sortBy,
      range,
      status,
    }),
    [date, notLogedIn, page, debouncedQuery, sortBy, range, status],
  );

  const { drivers, isDriverLoading } = useGetDriverSessions("", filter);

  const sessionMap: Record<string, any[]> = {};

  if (drivers?.items) {
    drivers.items.forEach((driver: Driver) => {
      sessionMap[driver.id] = driver.sessions || [];
    });
  }

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    setPage(1);
    setRange(RangeEnum.all);
    addQuery("date", newDate);
  };

  const handleRangeChange = (newRange: RangeEnum) => {
    setRange(newRange);
    setPage(1);
    setDate("");
    addQuery("range", newRange);
  };

  if (isDriverLoading)
    return (
      <div>
        <Loader />
      </div>
    );

  const totalItems = drivers?.meta?.totalItems;

  return (
    <section>
      {/* Title + count */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Sessions des chauffeurs</h5>
          {totalItems !== undefined && (
            <Chip tone="warning">
              {totalItems} {pluralize("Chauffeur", totalItems)}
            </Chip>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, padding: "12px 16px" }} className="mb-3">
        <nav className="d-flex flex-wrap align-items-end gap-3">
          <DateInput value={date} onChange={handleDateChange} />

          <FilterField label="Période">
            <PillSelect
              value={range ?? ""}
              onChange={(v) => handleRangeChange(v as RangeEnum)}
              options={[
                { value: RangeEnum.TODAY, label: "Aujourd'hui" },
                { value: RangeEnum.THIS_WEEK, label: "Semaine en cours" },
                { value: RangeEnum.THIS_MONTH, label: "Mois en cours" },
                { value: RangeEnum.LAST_3_MONTHS, label: "3 derniers mois" },
              ]}
            />
          </FilterField>

          <FilterField label="Connexion">
            <PillSelect
              value={notLogedIn ? String(notLogedIn) : ""}
              onChange={(v) => {
                const value = v ? parseInt(v, 10) : undefined;
                setNotLogedIn(value);
                setPage(1);
                addQuery("notLoggedIn", value);
              }}
              options={[
                { value: "1", label: "Non connecté" },
                { value: "2", label: "Connecté" },
              ]}
            />
          </FilterField>

          <FilterField label="Statut">
            <PillSelect
              value={status ?? ""}
              onChange={(v) => {
                setStatus(v as DriverProfileStatus);
                setPage(1);
              }}
              options={Object.entries(DriverStatusLabels).map(([k, label]) => ({
                value: k,
                label: String(label),
              }))}
            />
          </FilterField>

          <FilterField label="Recherche">
            <div
              className="d-flex align-items-center gap-2 px-3"
              style={controlStyle}
            >
              <MagnifyingGlassIcon color="gray" width={18} />
              <input
                type="text"
                className="border-0 w-100 bg-transparent"
                style={{ outline: "none", fontSize: 13 }}
                placeholder="Rechercher"
                value={searchQuery ?? ""}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </FilterField>
        </nav>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isDriverLoading}
          render={() =>
            drivers?.items && drivers.items.length > 0 ? (
              <SimpleDriversTable
                drivers={drivers.items}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                date={date}
                range={range}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      {drivers?.meta && (
        <Pagination
          currentPage={drivers.meta.currentPage}
          onPageChange={setPage}
          siblingCount={1}
          totalCount={drivers.meta.totalPages}
          pageSize={drivers.meta.itemsPerPage}
        />
      )}
    </section>
  );
}
